import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContactGroupSummary, CreateContactRequest, MessageContactPresentationDto } from "@multi-chat/shared";
import { createContact, listContactGroups } from "../../../api/contacts-api.js";
import { Button } from "../../../components/ui/button/index.js";
import { ContactFormSheet } from "../../contacts/ContactsPage/ContactFormSheet.js";
import { contactCardDisplayName, contactCardInitials, sharedContactCreateDefaults } from "../contact-card.js";

export function MessageContactPreview({
  open,
  onOpenChange,
  organizationId,
  presentation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  presentation: MessageContactPresentationDto;
}): React.JSX.Element {
  const { t } = useTranslation();
  const name = contactCardDisplayName(presentation) ?? t("inbox.messageType.CONTACT");
  const initials = contactCardInitials(presentation);
  const [saveOpen, setSaveOpen] = useState(false);
  const [groups, setGroups] = useState<ContactGroupSummary[]>([]);

  useEffect(() => {
    if (!saveOpen) {
      return;
    }
    let cancelled = false;
    void listContactGroups(organizationId).then((items) => {
      if (!cancelled) {
        setGroups(items);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, saveOpen]);

  const handleSave = useCallback(async (body: CreateContactRequest) => {
    await createContact(organizationId, body);
    setSaveOpen(false);
    onOpenChange(false);
  }, [onOpenChange, organizationId]);

  const rows: Array<{ label: string; value: string; dir?: "ltr" }> = [];
  if (presentation.firstName) {
    rows.push({ label: t("contacts.columns.firstName"), value: presentation.firstName });
  }
  if (presentation.lastName) {
    rows.push({ label: t("contacts.columns.lastName"), value: presentation.lastName });
  }
  if (presentation.primaryPhone) {
    rows.push({
      label: t("inbox.contact.phone"),
      value: presentation.primaryPhone,
      dir: "ltr",
    });
  }
  if (presentation.additionalPhoneCount) {
    rows.push({
      label: t("inbox.contact.morePhones"),
      value: t("inbox.contact.additionalPhones", { count: presentation.additionalPhoneCount }),
    });
  }
  if (presentation.primaryEmail) {
    rows.push({ label: t("contacts.columns.email"), value: presentation.primaryEmail });
  }
  if (presentation.organization) {
    rows.push({ label: t("inbox.contact.company"), value: presentation.organization });
  }

  return (
    <>
      <DialogPrimitive.Root
        open={open && !saveOpen}
        onOpenChange={(next) => {
          if (!next) {
            onOpenChange(false);
          }
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[var(--z-alert-overlay)] bg-ink/50 animate-overlay-in" />
          <DialogPrimitive.Content className="fixed inset-0 z-[var(--z-alert)] grid place-items-center p-4 outline-none">
            <div className="w-[min(22.5rem,100%)] rounded-2xl border border-line bg-surface-elevated p-5 shadow-lg">
              <DialogPrimitive.Title className="sr-only">
                {t("inbox.contact.previewTitle")}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                {t("inbox.contact.previewDescription")}
              </DialogPrimitive.Description>

              <div className="flex items-center gap-3">
                <span
                  className="flex size-12 shrink-0 items-center justify-center rounded-full bg-surface-muted text-[0.875rem] font-semibold text-ink"
                  aria-hidden="true"
                >
                  {initials}
                </span>
                <p className="m-0 min-w-0 truncate text-[1.0625rem] font-semibold text-ink">{name}</p>
              </div>

              {rows.length > 0 ? (
                <dl className="mt-4 grid gap-2">
                  {rows.map((row) => (
                    <div key={row.label} className="grid gap-0.5">
                      <dt className="text-[11px] uppercase tracking-wide text-ink-subtle">{row.label}</dt>
                      <dd className="m-0 text-[0.875rem] text-ink" dir={row.dir}>
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              <div className="mt-5 grid gap-2">
                <Button type="button" onClick={() => setSaveOpen(true)}>
                  {t("inbox.contact.save")}
                </Button>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  {t("inbox.saveContact.cancel")}
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <ContactFormSheet
        open={saveOpen}
        groups={groups}
        defaults={sharedContactCreateDefaults(presentation)}
        onClose={() => setSaveOpen(false)}
        onSubmit={handleSave}
      />
    </>
  );
}
